---
title: "How to shorten a title tag and meta description without losing search intent"
description: "A practical way to shorten title tags and meta descriptions: protect search intent first, then check character ranges without treating them as SERP guarantees."
locale: "en"
date: "2026-07-23"
tags: ["seo", "title tag", "meta description", "editing"]
relatedTools: ["seoLimits", "textCounter"]
translations:
  ru: "kak-napisat-title-i-description-bez-obrezaniya"
faq:
  - q: "Is there a strict character limit for title tags and meta descriptions?"
    a: "There is no single hard display limit. A search engine may truncate or rewrite a result based on the query, device and page content. Character ranges are useful editing guardrails."
  - q: "Which ranges does txtify show?"
    a: "The current checker uses 45–60 characters for a title and 120–160 for a description. These are practical ranges, not search-engine requirements or display guarantees."
  - q: "Does the tool check keywords or rankings?"
    a: "No. It counts visible characters and reports the range state. Query relevance, writing quality and future rankings require a separate review."
  - q: "Are the title and description uploaded?"
    a: "No. Counting runs locally in the browser. Analytics receives field lengths and range states, not the text you enter."
---

Do not cut characters from the end of a title just to turn the meter green.
First preserve the page topic and the user's search intent. Then remove
repetition, generic claims and, if necessary, the brand. For a description,
use a different order: answer, important constraint, action.

The [SEO limits checker](/seo-limity) uses these editing ranges:

| Field | txtify range | What should survive truncation |
| --- | ---: | --- |
| Title | 45–60 characters | The page topic and primary job |
| Description | 120–160 characters | The outcome, a material constraint and the next step |
| Snippet copy | 140–220 characters | A complete idea in the opening lines |
| Short preview | 70–110 characters | Meaning from the first words, without a filler opening |

These are not technical prohibitions. A search engine can display different
widths, take text from the page or rewrite a title for a particular query. The
counter warns when a field may be too short or may lose important words if
truncated; it does not predict rankings.

## Build the title around meaning first

A useful draft pattern is:

`[what the page is] + [job or meaningful difference] + [brand if needed]`

Weak:

`Home | A useful online service for working with documents`

It does not identify the page or the task.

Stronger:

`Compress PDF to 5 MB online — check the result`

Even if the tail disappears, the user still sees the action, format and target
size. The important words come before the brand and promotional extras.

When shortening a title, remove items in this order:

1. a repeated brand or category already obvious from the domain;
2. words such as `best`, `easy` and `modern` without a provable difference;
3. a second synonym for the same action;
4. context already obvious from the query;
5. the trailing brand when the remaining title is still precise.

Keep the format, conversion direction and numbers that change the job. `PDF to
JPG` and `JPG to PDF` are different pages. `To 5 MB` separates a target-size
workflow from generic compression.

## A description should not retell the title

The title names the page. The description explains what the user gets and what
they should know before clicking.

A practical structure is:

`[outcome] + [important limitation] + [action]`

For example:

`Reduce a PDF and see whether the result fits a 5 MB upload limit. The first
pass runs in the browser; stronger compression starts only when you choose it.`

It avoids keyword stuffing while keeping the outcome and processing limitation
clear.

If the description is too long, remove:

- greetings and `on this page you can`;
- a word-for-word repeat of the title;
- features that do not matter to the query;
- `learn more` calls that replace concrete information;
- the second sentence when the first already answers the job.

## Why 60 characters is not a magic boundary

Equal character counts do not occupy equal widths: `W` and `i`, digits and
emoji render differently. A phone and a wide display provide different space.
The search engine may also choose a heading or passage from the page.

txtify counts graphemes, which are closer to the visible characters a person
perceives. It does not measure pixels, simulate a result page or judge
relevance. For a separate view of words, lines and UTF-8 size, use the
[character counter](/schetchik-simvolov).

## Pre-publish check

- The opening of the title identifies the page without relying on its tail.
- Conversion direction and meaningful numbers remain intact.
- The description adds an outcome or limitation instead of repeating the
  title.
- Every promise matches the experience after the click.
- Both fields have been checked in [SEO limits](/seo-limity), without treating
  a green state as a display guarantee.
- The real result is reviewed after indexing because the engine may choose a
  different passage for a query.

Keep the old and new versions side by side. When changing metadata on a page
that already receives traffic, review impressions, click-through rate and query
relevance after recrawling, not just the new character count.
